import { Col, Divider, InputNumber, Row, Select, Slider } from "antd";
import React, { useCallback, useState } from "react";

const { Option } = Select;

const StructureFilter = (props) => {
  // Filter states
  const { termlist } = props;
  const [DntValue, setDntValue] = useState(25);
  const [fireClass, setFireClass] = useState([]);
  const [wallHeight, setWallHeight] = useState(3100);
  const [blockHeight, setBlockHeight] = useState(3700);
  const [hullClass, setHullClass] = useState([]);

  // Push options here
  const dntChildren = [];
  const hullChildren = [];

  dntChildren.push(
    <>
      {termlist.map((term) =>
        term.taxonomy === "rt_fire_resistance_class" ? (
          <Option value={term.name}>{term.name}</Option>
        ) : null,
      )}
    </>,
  );

  hullChildren.push(
    <>
      {termlist.map((term) =>
        term.taxonomy === "rt_hull_thickness" ? (
          <Option value={term.name}>{term.name}</Option>
        ) : null,
      )}
    </>,
  );

  // Handle logic to change values and filter
  const handleChange = useCallback(
    (dnt, fire, maxWallHeight, maxBlockHeight, hull) => {
      // Current filter values
      const currentFilters = {
        FireClass: fire,
        Dnt: dnt,
        maxWallHeight,
        maxBlockHeight,
        HullClass: hull,
      };

      const activeCurrentFilters = Object.keys(currentFilters).filter((key) => {
        if (Array.isArray(currentFilters[key])) {
          return currentFilters[key].length > 0;
        }

        return !!currentFilters[key];
      });

      const items = Array.from(
        document.getElementsByClassName("js-structure-item"),
      );

      // Go through all structure items and find each items all data-columns then filter values
      const itemsToHide = items.filter((item) => {
        const tabs = Array.from(item.querySelectorAll(`[data-column]`));

        const matches = Object.keys(currentFilters).filter((key) => {
          const filterClassValues = currentFilters[key];
          if (key === "FireClass") {
            const matchingItem = tabs.find(
              (tab) => tab.getAttribute("data-column") === "Paloluokka",
            );
            if (matchingItem) {
              const value = matchingItem.getAttribute("data-meta-value");

              return filterClassValues.includes(value);
            }
            return false;
          }

          if (key === "Dnt") {
            const matchingItem = tabs.find(
              (tab) =>
                tab.getAttribute("data-column") === "Äänitasoeroluku DnT,w",
            );
            if (matchingItem) {
              const value = matchingItem.getAttribute("data-meta-value");
              const strippedValue = value.replace(/\D/g, "");
              return filterClassValues <= strippedValue;
            }
            return false;
          }

          if (key === "maxWallHeight") {
            const matchingItem = tabs.find(
              (tab) =>
                tab.getAttribute("data-column") === "Max. seinän korkeus",
            );
            if (matchingItem) {
              const value = matchingItem.getAttribute("data-meta-value");
              const strippedValue = value.replace(/\D/g, "");
              return filterClassValues <= strippedValue;
            }
            return false;
          }

          if (key === "maxBlockHeight") {
            const matchingItem = tabs.find(
              (tab) =>
                tab.getAttribute("data-column") === "Max. korkeus osastoivana",
            );
            if (matchingItem) {
              const value = matchingItem.getAttribute("data-meta-value");
              const strippedValue = value.replace(/\D/g, "");
              return filterClassValues <= strippedValue;
            }
            return false;
          }

          if (key === "HullClass") {
            const matchingItem = tabs.find(
              (tab) => tab.getAttribute("data-column") === "Runkopaksuus",
            );
            if (matchingItem) {
              const value = matchingItem.getAttribute("data-meta-value");

              return filterClassValues.includes(value);
            }
            return false;
          }
        });

        return matches.length !== activeCurrentFilters.length;
      });

      // We need to remove class first for future filtering
      items.forEach((tab) => {
        tab.classList.remove("hidetop");
      });

      // Add hide class
      itemsToHide.forEach((tab) => {
        tab.classList.add("hidetop");
      });
    },
    [],
  );

  // Add selected items to DnT,W
  const dntOnChange = (value) => {
    setDntValue(value);
    handleChange(value, fireClass, wallHeight, blockHeight, hullClass);
  };

  // Add selected items to Paloluokka
  const fireClassOnChange = (value) => {
    setFireClass(value);
    handleChange(DntValue, value, wallHeight, blockHeight, hullClass);
  };

  // Remove items from Paloluokka
  const fireClassOnRemove = (value) => {
    const removeItemFromFireClass = fireClass.filter((item) => item !== value);
    setFireClass(removeItemFromFireClass);
    handleChange(
      DntValue,
      removeItemFromFireClass,
      wallHeight,
      blockHeight,
      hullClass,
    );
  };

  // Add selected items to Runkopaksuus
  const hullClassOnChange = (value) => {
    setHullClass(value);
    handleChange(DntValue, fireClass, wallHeight, blockHeight, value);
  };

  // Remove items from Runkopaksuus
  const hullClassOnRemove = (value) => {
    const removeItemFromHullClass = hullClass.filter((item) => item !== value);
    setHullClass(removeItemFromHullClass);
    handleChange(
      DntValue,
      fireClass,
      wallHeight,
      blockHeight,
      removeItemFromHullClass,
    );
  };

  // Add items to Max. seinän korkeus
  const wallHeightOnChange = (value) => {
    setWallHeight(value);
    handleChange(DntValue, fireClass, value, blockHeight, hullClass);
  };

  // Add items to Max. korkeus osastoivana
  const blockHeightOnChange = (value) => {
    setBlockHeight(value);
    handleChange(DntValue, fireClass, wallHeight, value, hullClass);
  };

  const clearFilters = () => {
    // Reset all filters to default values
    setDntValue(25);
    setFireClass([]);
    setWallHeight(3100);
    setBlockHeight(3700);
    setHullClass([]);
    handleChange(25, [], 3100, 3700, []);
  };

  return (
    <>
      <Row align="center" gutter={24}>
        <Col flex={1} style={{ marginBottom: "24px" }}>
          <span className="filter-heading">Paloluokka</span>
          <Divider
            style={{
              margin: "4px 0 16px 0",
              borderWidth: 1,
              borderColor: "#E0E0E0",
            }}
          />

          <Select
            value={fireClass}
            mode="multiple"
            style={{ width: "100%" }}
            defaultValue={fireClass}
            placeholder="Select"
            onChange={fireClassOnChange}
            onDeselect={fireClassOnRemove}
            showArrow="true"
          >
            {dntChildren}
          </Select>
        </Col>

        <Col flex={1} style={{ marginBottom: "24px" }}>
          <span className="filter-heading">Äänitasoeroluku DnT,w</span>
          <Divider
            style={{
              margin: "4px 0 16px 0",
              borderWidth: 1,
              borderColor: "#E0E0E0",
            }}
          />
          <Row justify="space-between">
            <Col span={14}>
              <Slider
                min={25}
                max={60}
                onChange={dntOnChange}
                value={typeof DntValue === "number" ? DntValue : 0}
              />
            </Col>
            <Col span={8}>
              <InputNumber
                style={{ width: "100%" }}
                min={25}
                max={60}
                value={DntValue}
                onChange={dntOnChange}
              />
            </Col>
          </Row>
        </Col>

        <Col flex={1} style={{ marginBottom: "24px" }}>
          <span className="filter-heading">Max. seinän korkeus</span>
          <Divider
            style={{
              margin: "4px 0 16px 0",
              borderWidth: 1,
              borderColor: "#E0E0E0",
            }}
          />
          <Row justify="space-between">
            <Col span={14}>
              <Slider
                min={3100}
                max={7300}
                onChange={wallHeightOnChange}
                value={typeof wallHeight === "number" ? wallHeight : 0}
              />
            </Col>
            <Col span={8}>
              <InputNumber
                style={{ width: "100%" }}
                min={3100}
                max={7300}
                value={wallHeight}
                onChange={wallHeightOnChange}
              />
            </Col>
          </Row>
        </Col>

        <Col flex={1} style={{ marginBottom: "24px" }}>
          <span className="filter-heading">Max. korkeus osastoivana</span>
          <Divider
            style={{
              margin: "4px 0 16px 0",
              borderWidth: 1,
              borderColor: "#E0E0E0",
            }}
          />
          <Row justify="space-between">
            <Col span={14}>
              <Slider
                min={3700}
                max={4700}
                onChange={blockHeightOnChange}
                value={typeof blockHeight === "number" ? blockHeight : 0}
              />
            </Col>
            <Col span={8}>
              <InputNumber
                style={{ width: "100%" }}
                min={3700}
                max={4700}
                value={blockHeight}
                onChange={blockHeightOnChange}
              />
            </Col>
          </Row>
        </Col>

        <Col flex={1} style={{ marginBottom: "24px" }}>
          <span className="filter-heading">Runkopaksuus</span>
          <Divider
            style={{
              margin: "4px 0 16px 0",
              borderWidth: 1,
              borderColor: "#E0E0E0",
            }}
          />

          <Select
            mode="multiple"
            style={{ width: "100%" }}
            defaultValue={hullClass}
            value={hullClass}
            placeholder="Select"
            onChange={hullClassOnChange}
            onDeselect={hullClassOnRemove}
            showArrow="true"
          >
            {hullChildren}
          </Select>
        </Col>

        <Col span={24}>
          <div className="clear">
            <span onClick={clearFilters}>Tyhjennä</span>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default StructureFilter;
